import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { AuthPage } from './pages/AuthPage';
import { Dashboard } from './pages/Dashboard';
import { AdminPanel } from './pages/AdminPanel';
import { InvitePage } from './pages/InvitePage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { CustomerPayment } from './pages/CustomerPayment';
import { CustomerDocumentUpload } from './pages/CustomerDocumentUpload';
import { NewPayment } from './pages/NewPayment';
import { Approvals } from './pages/Approvals';
import { MyProcessing } from './pages/MyProcessing';
import { MyFailedTransactions } from './pages/MyFailedTransactions';
import { PaymentLinks } from './pages/PaymentLinks';
import { RejectedTransactions } from './pages/RejectedTransactions';
import { PaymentHistory } from './pages/PaymentHistory';
import { Analytics } from './pages/Analytics';
import { PendingProcessing } from './pages/PendingProcessing';
import Processing from './pages/Processing';
import MyAssignedProcessing from './pages/MyAssignedProcessing';
import Deposits from './pages/Deposits';
import { UserManagement } from './pages/UserManagement';
import { Settings } from './pages/Settings';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AlertContainer } from './components/AlertContainer';
import { AlertService } from './services/AlertService';
import { ActivityTrackerWrapper } from './components/ActivityTrackerWrapper';
import { ADMIN_ROLES } from './utils/roleUtils';
import { ROLE_PERMISSIONS } from './constants/enums';
import { onForegroundMessage } from './firebase';
import { PortalAuthProvider } from './ip-portal/context/PortalAuthContext';
import { PortalLayout } from './ip-portal/components/PortalLayout';
import { PortalProtectedRoute } from './ip-portal/components/PortalProtectedRoute';
import { PortalLoginPage } from './ip-portal/pages/PortalLoginPage';
import { PortalSetupPage } from './ip-portal/pages/PortalSetupPage';
import { PortalDashboard } from './ip-portal/pages/PortalDashboard';
import { PortalDevices } from './ip-portal/pages/PortalDevices';
import { PortalAuditLog } from './ip-portal/pages/PortalAuditLog';
import { PortalInvite } from './ip-portal/pages/PortalInvite';
import { PortalInviteAccept } from './ip-portal/pages/PortalInviteAccept';
import { MyIpPage } from './ip-portal/pages/MyIpPage';
import './styles/theme.css';
import './App.css';

function App() {
  // Handle foreground push notifications
  useEffect(() => {
    const unsubscribe = onForegroundMessage((payload) => {
      console.log('Foreground notification received:', payload);

      // Show in-app notification
      const title = payload.notification?.title || 'New Notification';
      const body = payload.notification?.body || 'You have a new notification';
      AlertService.info(`${title}: ${body}`);

      // Also show browser notification if page is visible but not focused
      if (document.visibilityState === 'visible' && !document.hasFocus()) {
        if (Notification.permission === 'granted') {
          new Notification(title, {
            body,
            icon: '/logo192.png'
          });
        }
      }
    });

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <ActivityTrackerWrapper>
            <div className="App">
              <Routes>
              <Route path="/login" element={<AuthPage />} />
              <Route path="/signup" element={<AuthPage />} />
              <Route path="/invite" element={<InvitePage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/pay/:token" element={<CustomerPayment />} />
              <Route path="/docs/:token" element={<CustomerDocumentUpload />} />
              <Route 
                path="/dashboard" 
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/admin" 
                element={
                  <ProtectedRoute requiredRole={ADMIN_ROLES}>
                    <AdminPanel />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/payments/new" 
                element={
                  <ProtectedRoute requiredRole={ROLE_PERMISSIONS.canCreateSales}>
                    <NewPayment />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/approvals" 
                element={
                  <ProtectedRoute requiredRole={ROLE_PERMISSIONS.canApproveSales}>
                    <Approvals />
                  </ProtectedRoute>
                } 
              />
              <Route
                path="/my-pending-processing"
                element={
                  <ProtectedRoute>
                    <MyProcessing variant="pending" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-processed"
                element={
                  <ProtectedRoute>
                    <MyProcessing variant="processed" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-chargebacks"
                element={
                  <ProtectedRoute>
                    <MyProcessing variant="terminal" />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/payment-links"
                element={
                  <ProtectedRoute>
                    <PaymentLinks />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-failed-transactions"
                element={
                  <ProtectedRoute>
                    <MyFailedTransactions />
                  </ProtectedRoute>
                }
              />
              <Route 
                path="/rejected-transactions" 
                element={
                  <ProtectedRoute>
                    <RejectedTransactions />
                  </ProtectedRoute>
                } 
              />
              <Route
                path="/transactions"
                element={
                  <ProtectedRoute>
                    <PaymentHistory />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analytics"
                element={
                  <ProtectedRoute requiredRole={['sales_team_lead', 'processor_team_lead', 'floor_manager', 'director', 'admin']}>
                    <Analytics />
                  </ProtectedRoute>
                }
              />
              <Route 
                path="/pending-processing" 
                element={
                  <ProtectedRoute requiredRole={['processor_team_lead', 'floor_manager', 'director', 'admin']}>
                    <PendingProcessing />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/processing" 
                element={
                  <ProtectedRoute requiredRole={['processor_team_lead', 'floor_manager', 'director', 'admin']}>
                    <Processing />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/my-assigned-processing" 
                element={
                  <ProtectedRoute requiredRole={['processor_agent', 'processor_team_lead', 'floor_manager', 'director', 'admin']}>
                    <MyAssignedProcessing />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/deposits"
                element={
                  <ProtectedRoute requiredRole={['processor_agent', 'processor_team_lead', 'floor_manager', 'director', 'admin']}>
                    <Deposits />
                  </ProtectedRoute>
                }
              />
              <Route 
                path="/user-management" 
                element={
                  <ProtectedRoute requiredRole={ROLE_PERMISSIONS.canManageUsers}>
                    <UserManagement />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/settings" 
                element={
                  <ProtectedRoute requiredRole={['admin', 'director', 'floor_manager', 'sales_team_lead']}>
                    <Settings />
                  </ProtectedRoute>
                } 
              />
              {/* IP Portal routes — separate auth context, own layout */}
              <Route path="/ip-portal" element={
                <PortalAuthProvider>
                  <PortalLayout />
                </PortalAuthProvider>
              }>
                <Route path="login" element={<PortalLoginPage />} />
                <Route path="setup" element={<PortalSetupPage />} />
                <Route path="my-ip" element={<MyIpPage />} />
                <Route path="invite/:token" element={<PortalInviteAccept />} />
                <Route path="dashboard" element={
                  <PortalProtectedRoute><PortalDashboard /></PortalProtectedRoute>
                } />
                <Route path="devices" element={
                  <PortalProtectedRoute><PortalDevices /></PortalProtectedRoute>
                } />
                <Route path="audit" element={
                  <PortalProtectedRoute><PortalAuditLog /></PortalProtectedRoute>
                } />
                <Route path="invite" element={
                  <PortalProtectedRoute requireSuperAdmin><PortalInvite /></PortalProtectedRoute>
                } />
                <Route index element={<Navigate to="/ip-portal/dashboard" replace />} />
              </Route>

              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
              <AlertContainer />
            </div>
          </ActivityTrackerWrapper>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
